import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, MessageCircle, ArrowRight, Star, CheckCircle2, ShieldCheck,
  Sparkles, Microscope, ClipboardCheck, Clock, HeartHandshake, Award,
} from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQAccordion from "@/components/FAQAccordion";
import AppointmentForm from "@/components/AppointmentForm";
import InsurersStrip from "@/components/InsurersStrip";
import { JsonLd, medicalProcedureSchema } from "@/components/StructuredData";
import { CLINIC } from "@/lib/clinicInfo";
import { findService } from "@/data/services";

const SYMPTOMS = [
  "Cloudy or blurred vision — especially at distance",
  "Glare and halos around lights at night",
  "Faded or yellow-tinted colours",
  "Difficulty reading in low light",
  "Frequent change of spectacle number",
  "Double vision in one eye",
];

const WHY_NEEDED = [
  { title: "It's the only real fix", desc: "There is no eye drop, laser or exercise that can reverse a cataract — the cloudy natural lens has to be replaced with a clear IOL." },
  { title: "Vision keeps declining", desc: "Waiting doesn't help. Cataracts get denser, glare worsens, and the surgery itself becomes technically harder." },
  { title: "Falls and safety", desc: "Poor contrast and night vision meaningfully raise the risk of falls, missed steps and driving mishaps." },
  { title: "Modern surgery is safe & day-care", desc: "15-minute painless procedure, home the same morning, and functional vision often the next day." },
];

const WHY_NEHCHAL = [
  { icon: <Award className="h-5 w-5" />, t: "Fellowship-Trained Surgeon", d: "MS Ophthalmology (KGMU) · Fellowship at LVPEI Hyderabad — every cataract personally performed by Dr. Gurkiran Kaur." },
  { icon: <HeartHandshake className="h-5 w-5" />, t: "Personally Led Care", d: "One surgeon from consultation to final follow-up. No hand-offs, no rotating juniors." },
  { icon: <Microscope className="h-5 w-5" />, t: "Advanced Phaco OT", d: "2.2 mm micro-incision phacoemulsification with a modern surgical microscope." },
  { icon: <ClipboardCheck className="h-5 w-5" />, t: "Precision IOL Planning", d: "Optical biometry (IOL Master), corneal topography and personalised IOL recommendation." },
  { icon: <ShieldCheck className="h-5 w-5" />, t: "Cashless Insurance", d: "End-to-end paperwork support for 30+ major insurers and TPAs." },
  { icon: <Sparkles className="h-5 w-5" />, t: "Complex Cases Welcome", d: "Hyper-mature, subluxated, post-uveitic and post-RK cataracts routinely managed." },
];

const IOL_OPTIONS = [
  { name: "Monofocal", best: "Distance vision, reading glasses acceptable", pros: "Sharpest single-distance vision, most affordable" },
  { name: "Toric", best: "Significant astigmatism (>1.0 D)", pros: "Corrects astigmatism during surgery — no cylindrical glasses" },
  { name: "Multifocal", best: "Freedom from glasses for near & far", pros: "Two focal points — reduces dependency on readers" },
  { name: "Trifocal / EDOF", best: "Maximum spectacle independence at all distances", pros: "Sharp far, intermediate (screen) and near vision" },
];

const LENS_FACTORS = [
  "Your daily activities — driving, reading, screens, sport",
  "Corneal health and any dry eye",
  "Astigmatism (measured with topography)",
  "Pupil size in dim light",
  "Retina and macular health on OCT",
  "Realistic expectations discussed one-to-one",
];

const SURGERY_STEPS = [
  { n: "01", t: "Anaesthesia", d: "Numbing drops — no injections, no general anaesthesia for routine cases" },
  { n: "02", t: "Micro-incision", d: "2.2 mm self-sealing corneal opening — no sutures" },
  { n: "03", t: "Phacoemulsification", d: "Ultrasonic breakdown and gentle removal of the cloudy lens" },
  { n: "04", t: "IOL Implantation", d: "Foldable premium IOL placed inside the natural capsule" },
  { n: "05", t: "Discharge", d: "Home within 30 minutes of finishing surgery, with medications and shield" },
];

const RECOVERY = [
  { day: "Day 1", note: "Follow-up visit. Vision usually functional. Start eye drops." },
  { day: "Week 1", note: "Most patients read, watch TV and do light housework. Avoid dust and swimming." },
  { day: "Week 4", note: "Final spectacle prescription (if needed). Return to full activity." },
  { day: "Month 3", note: "Final settled vision. Second eye can now be planned if applicable." },
];

const STORIES = CLINIC.testimonials
  .filter((t) => /cataract/i.test(t.role))
  .slice(0, 3);

export default function CataractLanding() {
  const service = findService("cataract-surgery");
  return (
    <>
      <Seo
        title="Cataract Surgery in Noida — Dr. Gurkiran Kaur · Premium IOLs"
        description="Advanced cataract surgery in Sector 32 Noida by fellowship-trained ophthalmologist Dr. Gurkiran Kaur. Micro-incision phaco, monofocal / toric / multifocal / trifocal IOL options, cashless insurance."
        path="/cataract"
      />
      {service ? <JsonLd data={medicalProcedureSchema(service, CLINIC)} /> : null}
      <Breadcrumbs items={[{ name: "Cataract Surgery", path: "/cataract" }]} />

      {/* HERO */}
      <section className="container-wide pt-8 pb-12">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Cataract Surgery in Noida</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
              Clearer vision, <span className="text-primary italic">personally handled.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-foreground/75 max-w-xl leading-7">
              Advanced micro-incision cataract surgery in Sector 32 Noida, personally performed by <strong className="text-foreground">Dr. Gurkiran Kaur</strong> — with the right premium IOL matched to your lifestyle.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground/70">
              <span className="flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </span>
              <span><strong className="text-foreground">{CLINIC.ratingValue}</strong> · {CLINIC.reviewCount}+ Google reviews</span>
              <span>·</span>
              <span>Cashless insurance</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#book" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90" data-testid="cataract-book-hero">
                Book Cataract Consultation <ArrowRight className="h-4 w-4" />
              </a>
              <a href={`tel:${CLINIC.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary" data-testid="cataract-call-hero">
                <Phone className="h-4 w-4" /> Call {CLINIC.phone}
              </a>
              <a href={CLINIC.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm text-primary" data-testid="cataract-wa-hero">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
              <div className="text-xs uppercase tracking-widest text-accent mb-2">Quick enquiry</div>
              <div className="font-serif text-xl mb-4">Book a cataract evaluation</div>
              <AppointmentForm defaultService="Cataract Surgery in Noida" />
            </div>
          </aside>
        </div>
      </section>

      {/* SYMPTOMS */}
      <section className="border-y border-border bg-secondary/40 py-14" data-testid="cataract-symptoms">
        <div className="container-wide">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Symptoms</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Common cataract symptoms.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SYMPTOMS.map((s) => (
              <div key={s} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /> <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY NEEDED */}
      <section className="container-wide py-16" data-testid="cataract-why-needed">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Why Surgery</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Why cataract surgery is needed.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY_NEEDED.map((w) => (
            <div key={w.title} className="rounded-xl border border-border bg-card p-5">
              <div className="font-serif text-lg text-foreground">{w.title}</div>
              <p className="mt-2 text-sm text-foreground/70 leading-6">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY NEHCHAL */}
      <section className="bg-secondary/40 border-y border-border py-16" data-testid="cataract-why-nehchal">
        <div className="container-wide">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Why Nehchal</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Why patients trust us with their cataract surgery.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_NEHCHAL.map((w) => (
              <div key={w.t} className="rounded-xl border border-border bg-card p-5">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">{w.icon}</div>
                <div className="font-serif text-lg">{w.t}</div>
                <p className="mt-2 text-sm text-foreground/70 leading-6">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section className="container-wide py-16" data-testid="cataract-technology">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Surgical Technology</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Precision equipment for a routine procedure.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { t: "Micro-incision Phaco Unit", d: "2.2 mm sutureless surgery" },
            { t: "IOL Master (Optical Biometry)", d: "Sub-millimetre IOL power accuracy" },
            { t: "Corneal Topography", d: "Astigmatism mapping for toric IOL planning" },
            { t: "OCT (Retina & Macula)", d: "Rules out macular disease before premium IOLs" },
            { t: "High-magnification Microscope", d: "Modern optics for delicate manoeuvres" },
            { t: "Fully-equipped OT & Recovery", d: "In-house day-care setup, no external transfer" },
          ].map((x) => (
            <div key={x.t} className="rounded-xl border border-border bg-card p-5">
              <div className="font-serif text-base">{x.t}</div>
              <p className="text-sm text-foreground/65 mt-1 leading-6">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IOL OPTIONS */}
      <section className="bg-secondary/40 border-y border-border py-16" data-testid="cataract-iols">
        <div className="container-wide">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-accent mb-3">IOL Options</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-foreground">Your lens options.</h2>
            </div>
            <Link
              to="/cataract/iol-guide"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90"
              data-testid="iol-guide-link"
            >
              Download Your IOL Guide (PDF) <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {IOL_OPTIONS.map((iol) => (
              <div key={iol.name} className="rounded-xl border border-border bg-card p-6">
                <div className="font-serif text-xl text-primary">{iol.name}</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-2">Best for</div>
                <p className="text-sm text-foreground/80 mt-1">{iol.best}</p>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-3">Advantage</div>
                <p className="text-sm text-foreground/80 mt-1">{iol.pros}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LENS SELECTION */}
      <section className="container-wide py-16" data-testid="cataract-lens-selection">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Lens Selection</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-6">How we select your lens.</h2>
        <p className="text-foreground/70 leading-7 max-w-2xl mb-8">
          There is no &ldquo;best&rdquo; IOL — only the one that fits your eyes and your daily life. Dr. Kaur personally reviews six factors before recommending yours.
        </p>
        <ul className="grid sm:grid-cols-2 gap-3">
          {LENS_FACTORS.map((f) => (
            <li key={f} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
      </section>

      {/* WHAT SURGERY INVOLVES */}
      <section className="bg-secondary/40 border-y border-border py-16" data-testid="cataract-steps">
        <div className="container-wide">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">What Surgery Involves</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-10">Five steps, roughly fifteen minutes.</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {SURGERY_STEPS.map((s) => (
              <div key={s.n} className="rounded-xl border border-border bg-card p-5">
                <div className="text-primary font-serif text-2xl">{s.n}</div>
                <div className="font-medium mt-1">{s.t}</div>
                <p className="text-xs text-foreground/65 mt-2 leading-6">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOVERY */}
      <section className="container-wide py-16" data-testid="cataract-recovery">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Recovery</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">What to expect after surgery.</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {RECOVERY.map((r) => (
            <div key={r.day} className="rounded-xl border border-border bg-card p-5">
              <div className="inline-flex items-center gap-1.5 text-primary text-sm"><Clock className="h-3.5 w-3.5" /> {r.day}</div>
              <p className="mt-2 text-sm text-foreground/75 leading-6">{r.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      {service ? <FAQAccordion faqs={service.faqs} title="Cataract surgery — your questions answered" /> : null}

      {/* INSURERS STRIP */}
      <InsurersStrip compact />

      {/* PATIENT STORIES */}
      {STORIES.length > 0 && (
        <section className="container-wide py-16" data-testid="cataract-stories">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Patient Stories</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Real patients, real outcomes.</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {STORIES.map((t) => (
              <blockquote key={t.name} className="rounded-xl border border-border bg-card p-6">
                <div className="flex items-center gap-1 text-amber-500 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="text-sm text-foreground/80 italic leading-6">&ldquo;{t.text}&rdquo;</p>
                <footer className="mt-4 text-sm">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}

      {/* BOOK */}
      <section id="book" className="bg-secondary/40 border-t border-border py-20" data-testid="cataract-book">
        <div className="container-wide grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Book Consultation</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-4">Discuss your cataract in a single visit.</h2>
            <p className="text-foreground/70 leading-7 mb-6">
              A one-time evaluation gives you a clear surgical plan, IOL recommendation and cashless quote.
            </p>
            <a href={`tel:${CLINIC.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90">
              <Phone className="h-4 w-4" /> Call {CLINIC.phone}
            </a>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <AppointmentForm defaultService="Cataract Surgery in Noida" />
          </div>
        </div>
      </section>
    </>
  );
}
